import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import DashboardLayout from '../dashboard/components/DashboardLayout';
import InventoryOverview from './components/InventoryOverview';
import InventoryDetail from './components/InventoryDetail';
import StockAlert from './components/StockAlert';

export interface InventoryItem {
  id: string;
  productId: string;
  productName: string;
  category: string;
  size: string;
  color: string;
  quantity: number;
  minStock: number;
  maxStock: number;
  unitPrice: number;
  totalValue: number;
  lastUpdated: string;
  status: 'in_stock' | 'low_stock' | 'out_of_stock';
  image?: string;
}

export interface InventoryStats {
  totalProducts: number;
  totalVariants: number;
  totalQuantity: number;
  totalValue: number;
  lowStockItems: number;
  outOfStockItems: number;
  categories: { [key: string]: number };
}

export default function Inventory() {
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [stats, setStats] = useState<InventoryStats>({
    totalProducts: 0,
    totalVariants: 0,
    totalQuantity: 0,
    totalValue: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    categories: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<'overview' | 'detail' | 'alerts'>('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const categories = ['Quần Áo', 'Vợt Thi Đấu', 'Phụ kiện thi đấu', 'Phụ kiện khác'];

  // Load inventory data from Supabase
  const loadInventoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load products with variants
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select(`
          id,
          name,
          category,
          base_price,
          images,
          updated_at,
          product_variants (
            id,
            size,
            color,
            stock_quantity,
            image_url,
            images
          )
        `)
        .eq('is_active', true);

      if (productsError) {
        throw productsError;
      }

      // Transform data to inventory items
      const inventoryItems: InventoryItem[] = [];
      let totalProducts = 0;
      let totalVariants = 0;
      let totalQuantity = 0;
      let totalValue = 0;
      let lowStockItems = 0;
      let outOfStockItems = 0;
      const categoryCounts: { [key: string]: number } = {};

      (productsData || []).forEach(product => {
        totalProducts++;
        
        if (!categoryCounts[product.category]) {
          categoryCounts[product.category] = 0;
        }
        categoryCounts[product.category]++;

        (product.product_variants || []).forEach(variant => {
          totalVariants++;
          totalQuantity += variant.stock_quantity;
          const itemValue = variant.stock_quantity * product.base_price;
          totalValue += itemValue;

          let status: 'in_stock' | 'low_stock' | 'out_of_stock' = 'in_stock';
          if (variant.stock_quantity === 0) {
            status = 'out_of_stock';
            outOfStockItems++;
          } else if (variant.stock_quantity <= 5) {
            status = 'low_stock';
            lowStockItems++;
          }

          const images = variant.images ? JSON.parse(variant.images) : [];
          const productImages = product.images ? JSON.parse(product.images) : [];
          const image = images[0] || variant.image_url || productImages[0] || '';

          inventoryItems.push({
            id: variant.id,
            productId: product.id,
            productName: product.name,
            category: product.category || 'Khác',
            size: variant.size,
            color: variant.color,
            quantity: variant.stock_quantity,
            minStock: 5, // Default minimum stock
            maxStock: 100, // Default maximum stock
            unitPrice: product.base_price,
            totalValue: itemValue,
            lastUpdated: product.updated_at,
            status,
            image
          });
        });
      });

      setInventoryItems(inventoryItems);
      setStats({
        totalProducts,
        totalVariants,
        totalQuantity,
        totalValue,
        lowStockItems,
        outOfStockItems,
        categories: categoryCounts
      });

    } catch (err: any) {
      console.error('Error loading inventory data:', err);
      setError('Không thể tải dữ liệu kho hàng: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInventoryData();
  }, []);

  // Filter inventory items
  const filteredItems = inventoryItems.filter(item => {
    const matchesSearch = 
      item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.size.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.color.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !filterCategory || item.category === filterCategory;
    const matchesStatus = !filterStatus || item.status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Update stock quantity
  const handleUpdateStock = async (variantId: string, newQuantity: number) => {
    try {
      const { error } = await supabase
        .from('product_variants')
        .update({ stock_quantity: newQuantity })
        .eq('id', variantId);

      if (error) {
        throw error;
      }

      // Reload data
      await loadInventoryData();
    } catch (err: any) {
      console.error('Error updating stock:', err);
      alert('Có lỗi khi cập nhật số lượng: ' + err.message);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Đang tải dữ liệu kho hàng...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-red-600 text-2xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Có lỗi xảy ra</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadInventoryData}
            className="bg-orange-600 text-white hover:bg-orange-700 px-4 py-2 rounded-lg"
          >
            Thử lại
          </button>
        </div>
      );
    }

    switch (currentView) {
      case 'detail':
        return (
          <InventoryDetail
            items={filteredItems}
            onUpdateStock={handleUpdateStock}
            onBack={() => setCurrentView('overview')}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filterCategory={filterCategory}
            onFilterCategoryChange={setFilterCategory}
            filterStatus={filterStatus}
            onFilterStatusChange={setFilterStatus}
            categories={categories}
          />
        );
      case 'alerts':
        return (
          <StockAlert
            items={inventoryItems.filter(item => item.status === 'low_stock' || item.status === 'out_of_stock')}
            onUpdateStock={handleUpdateStock}
            onBack={() => setCurrentView('overview')}
          />
        );
      default:
        return (
          <InventoryOverview
            stats={stats}
            items={inventoryItems}
            onViewDetail={() => setCurrentView('detail')}
            onViewAlerts={() => setCurrentView('alerts')}
          />
        );
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Quản lý Kho</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Theo dõi và quản lý tồn kho chi tiết theo từng sản phẩm</p>
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center space-x-1 sm:space-x-2 w-full sm:w-auto">
            <button
              onClick={() => setCurrentView('overview')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${
                currentView === 'overview'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-dashboard-line mr-1 sm:mr-2"></i>
              <span className="hidden sm:inline">Tổng quan</span>
              <span className="sm:hidden">Tổng quan</span>
            </button>
            <button
              onClick={() => setCurrentView('detail')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${
                currentView === 'detail'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-list-check-line mr-1 sm:mr-2"></i>
              <span className="hidden sm:inline">Chi tiết</span>
              <span className="sm:hidden">Chi tiết</span>
            </button>
            <button
              onClick={() => setCurrentView('alerts')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap relative ${
                currentView === 'alerts'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-alarm-warning-line mr-1 sm:mr-2"></i>
              <span className="hidden sm:inline">Cảnh báo</span>
              <span className="sm:hidden">Cảnh báo</span>
              {(stats.lowStockItems + stats.outOfStockItems) > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center">
                  {stats.lowStockItems + stats.outOfStockItems}
                </span>
              )}
            </button>
          </div>
        </div>

        {renderContent()}
      </div>
    </DashboardLayout>
  );
}
